import { MonitoringEvent } from '@kitopi-com/payments';
import { API_EVENTS_KEY, API_EVENTS_URL } from '@/constants/config';
import { APP_TYPE } from '@/constants/constants';
import i18n from '@/i18n';
import { fetch } from '@/utils/fetch';
import { getLocale } from '@/utils/getLocale';
import { version } from '../../../package.json';

const getBrowserUserId = (): string | null => {
  try {
    return localStorage.getItem('browserUserId');
  } catch {
    return null;
  }
};

// Global variables to store payment data
let lastPaymentMethod: string | null = null;
let lastPaymentError: { errorCode: string; errorMessage: string } | null = null;
let paymentStartTimestamp: number | null = null;
let paymentEndTimestamp: number | null = null;

// Split bill data type
type SplitBillDataType = {
  splitBillEnabled: boolean;
  numberOfSplits: number;
  payingFor: number;
  orderValue: number;
  serviceFeeAmount: number;
  brandName: string;
  orderId: string;
};

// Split bill data for money hash events
let splitBillData: SplitBillDataType | null = null;

// Initialize failed payments counter in localStorage
const getFailedPaymentsCount = (): number => {
  const count = localStorage.getItem('numberOfFailedPaymentsInARow');
  return count ? parseInt(count, 10) : 0;
};

const incrementFailedPaymentsCount = (): void => {
  const currentCount = getFailedPaymentsCount();
  localStorage.setItem('numberOfFailedPaymentsInARow', (currentCount + 1).toString());
};

const resetFailedPaymentsCount = (): void => {
  localStorage.setItem('numberOfFailedPaymentsInARow', '0');
};

// Intercept fetch calls to capture payment errors
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);

  // Check if this is a payment API call
  const url = args[0]?.toString() || '';
  if (url.includes('payments-api') && url.includes('payments?external-id=')) {
    try {
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();

      if (data.status === 'FAILED' && data.errorCode && data.errorMessage) {
        lastPaymentError = {
          errorCode: data.errorCode,
          errorMessage: data.errorMessage,
        };
      }
    } catch {
      // Ignore JSON parsing errors
    }
  }

  return response;
};

// Map MoneyHash payment methods to readable codes
const mapPaymentMethodToCode = (paymentMethod: string): string => {
  switch (paymentMethod) {
    case 'GOOGLE_PAY':
      return 'Google Pay';
    case 'APPLE_PAY':
      return 'Apple Pay';
    case 'CARD':
    case 'CREDIT_CARD':
    case 'DEBIT_CARD':
      return 'card';
    default:
      return 'other';
  }
};

export const getLastPaymentMethod = () => lastPaymentMethod;
const clearLastPaymentMethod = () => {
  lastPaymentMethod = null;
  lastPaymentError = null;
};

export { getFailedPaymentsCount, resetFailedPaymentsCount };

// Payment timestamp management
export const setPaymentStartTimestamp = (): void => {
  paymentStartTimestamp = Date.now();
};

export const setPaymentEndTimestamp = (): void => {
  paymentEndTimestamp = Date.now();
};

export const getPaymentTimestamps = (): {
  paymentStartTimestamp: number | null;
  paymentEndTimestamp: number | null;
} => ({
  paymentStartTimestamp: paymentStartTimestamp,
  paymentEndTimestamp: paymentEndTimestamp,
});

export const getPaymentProcessLatency = (): number | null => {
  if (paymentStartTimestamp && paymentEndTimestamp) {
    // Return latency in seconds (rounded to 2 decimal places)
    return Math.round((paymentEndTimestamp - paymentStartTimestamp) / 100) / 10;
  }
  return null;
};

export const clearPaymentTimestamps = (): void => {
  paymentStartTimestamp = null;
  paymentEndTimestamp = null;
};

// Split bill data management
export const setSplitBillData = (data: SplitBillDataType) => {
  splitBillData = data;
};

const clearSplitBillData = (): void => {
  splitBillData = null;
};

// System information utilities
export const getSystemInfo = (): { platform: string; userAgent: string; osVersion?: string } => {
  const platform = navigator.platform || 'unknown';
  const userAgent = navigator.userAgent || 'unknown';

  // Try to extract OS version from user agent
  let osVersion: string | undefined;

  // iOS version detection
  const iosMatch = userAgent.match(/OS (\d+_\d+)/);
  if (iosMatch) {
    osVersion = `iOS ${iosMatch[1].replace('_', '.')}`;
  }

  // Android version detection
  const androidMatch = userAgent.match(/Android (\d+(?:\.\d+)?)/);
  if (androidMatch) {
    osVersion = `Android ${androidMatch[1]}`;
  }

  // Windows version detection
  const windowsMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
  if (windowsMatch) {
    const version = parseFloat(windowsMatch[1]);
    if (version === 10.0) osVersion = 'Windows 10';
    else if (version === 6.3) osVersion = 'Windows 8.1';
    else if (version === 6.2) osVersion = 'Windows 8';
    else if (version === 6.1) osVersion = 'Windows 7';
    else osVersion = `Windows ${version}`;
  }

  // macOS version detection
  const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+)/);

  if (macMatch) {
    osVersion = `macOS ${macMatch[1].replace('_', '.')}`;
  }

  return {
    platform,
    userAgent,
    osVersion,
  };
};

// Outlet context - static values that don't change during user session
type OutletContextType = {
  brandId?: string;
  outletId?: string;
  country?: string;
  currency?: string;
};

const OUTLET_CONTEXT_KEY = 'dynatrace_outlet_context';

// Initialize from sessionStorage on module load
const getStoredOutletContext = (): OutletContextType => {
  try {
    const stored = sessionStorage.getItem(OUTLET_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

let outletContext: OutletContextType = getStoredOutletContext();

export const setOutletContext = (context: OutletContextType): void => {
  outletContext = { ...context };
  try {
    sessionStorage.setItem(OUTLET_CONTEXT_KEY, JSON.stringify(outletContext));
  } catch {
    // sessionStorage might be unavailable in some contexts
  }
};

// Tip context - stores only the selected tip tier for payment events
// Note: tipAmount is already sent by MoneyHash SDK as 'tip', so we only need the tier
// Stored in sessionStorage to persist across page reloads
const TIP_PERCENTAGE_KEY = 'dynatrace_tip_percentage';

const getStoredTipPercentage = (): number | 'custom' | null => {
  try {
    const stored = sessionStorage.getItem(TIP_PERCENTAGE_KEY);
    if (stored === null) return null;
    return stored === 'custom' ? 'custom' : Number(stored);
  } catch {
    return null;
  }
};

let tipPercentageContext: number | 'custom' | null = getStoredTipPercentage();

export const setTipPercentage = (tipPercentage: number | 'custom'): void => {
  tipPercentageContext = tipPercentage;
  try {
    sessionStorage.setItem(TIP_PERCENTAGE_KEY, String(tipPercentage));
  } catch {
    // sessionStorage might be unavailable
  }
};

const clearTipPercentage = (): void => {
  tipPercentageContext = null;
  try {
    sessionStorage.removeItem(TIP_PERCENTAGE_KEY);
  } catch {
    // sessionStorage might be unavailable
  }
};

// Split bill context - always includes isSplitBill for easy Dynatrace filtering
type SplitBillContextType = {
  isSplitBill: boolean;
  numberOfSplits?: number;
  payingFor?: number;
};

const SPLIT_BILL_CONTEXT_KEY = 'dynatrace_split_bill_context';

const getStoredSplitBillContext = (): SplitBillContextType => {
  try {
    const stored = sessionStorage.getItem(SPLIT_BILL_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : { isSplitBill: false };
  } catch {
    return { isSplitBill: false };
  }
};

let splitBillContext: SplitBillContextType = getStoredSplitBillContext();

export const setSplitBillContext = (numberOfSplits: number, payingFor: number): void => {
  splitBillContext = {
    isSplitBill: true,
    numberOfSplits,
    payingFor,
  };
  try {
    sessionStorage.setItem(SPLIT_BILL_CONTEXT_KEY, JSON.stringify(splitBillContext));
  } catch {
    // sessionStorage might be unavailable
  }
};

const clearSplitBillContext = (): void => {
  splitBillContext = { isSplitBill: false };
  try {
    sessionStorage.removeItem(SPLIT_BILL_CONTEXT_KEY);
  } catch {
    // sessionStorage might be unavailable
  }
};

/** A payment attempt reached a terminal state — drop everything scoped to it. */
export const clearPaymentSession = (): void => {
  clearLastPaymentMethod();
  clearSplitBillData();
  clearTipPercentage();
  clearSplitBillContext();
};

// Helper function to send Dynatrace events with common base data
export const sendBusinessEvent = (
  eventType: string,
  attributes: Record<string, unknown> = {}
): void => {
  const dataToSend = {
    timestamp: new Date().toISOString(),
    type: eventType,
    category: APP_TYPE,
    provider: location.hostname,
    event: {
      id: crypto.randomUUID(),
    },
    browser: {
      user_agent: navigator.userAgent,
    },
    appId: APP_TYPE,
    appVersion: version,
    browserUserId: getBrowserUserId(),
    language: i18n.language || 'en',
    locale: getLocale(i18n.language, outletContext.country),
    ...outletContext,
    ...splitBillContext,
    ...attributes,
  };

  fetch
    .post(API_EVENTS_URL, JSON.stringify([dataToSend]), {
      headers: { 'x-bizevents-api-key': API_EVENTS_KEY },
    })
    .catch(() => {
      // Fire-and-forget: a failing events API must never reject into the app
    });
};

export const monitoring = ({ type, attributes }: MonitoringEvent) => {
  const dataWithAppId = {
    appId: APP_TYPE,
    appVersion: version,
    browserUserId: getBrowserUserId(),
    language: i18n.language || 'en',
    locale: getLocale(i18n.language, outletContext.country),
    ...outletContext,
    ...splitBillContext,
    ...(tipPercentageContext !== null && { tipPercentage: tipPercentageContext }),
    ...attributes,
  };

  // Save payment method from transaction events
  if (type === 'transaction' && attributes.paymentMethod) {
    lastPaymentMethod = mapPaymentMethodToCode(attributes.paymentMethod);
  }

  // Handle payment failures and send GTM event
  // Note: Using 'purchase_failed' event name to match PaymentFailure component
  if (
    type === 'transaction' &&
    attributes.eventName === 'paymentFailed' &&
    typeof window.gtag === 'function'
  ) {
    // Increment failed payments counter
    incrementFailedPaymentsCount();

    const sendFailureEvent = () => {
      // Set payment end timestamp if not already set
      if (!paymentEndTimestamp) {
        paymentEndTimestamp = Date.now();
      }

      const failureData = {
        event: 'purchase_failed',
        sourceScreen: 'checkout',
        paymentMethod: lastPaymentMethod || 'unknown',
        errorCode: lastPaymentError?.errorCode || attributes.errorCode || 'unknown',
        errorMessage:
          lastPaymentError?.errorMessage ||
          attributes.errorMessage ||
          attributes.message ||
          'Payment failed',
        merchantReference: attributes.merchantReference || 'unknown',
        payableAmount: attributes.payableAmount || 0,
        currency: attributes.currency || outletContext.currency || 'AED',
        numberOfFailedPaymentsInARow: getFailedPaymentsCount(),
        paymentStartTimestamp: paymentStartTimestamp,
        paymentEndTimestamp: paymentEndTimestamp,
        paymentProcessLatency: getPaymentProcessLatency(),
        ...getSystemInfo(),
        // Split bill data - matching CheckoutController event structure
        split_bill_enabled: splitBillData?.splitBillEnabled || false,
        number_of_splits: splitBillData?.numberOfSplits || 0,
        paying_for: splitBillData?.payingFor || 0,
        order_value: splitBillData?.orderValue || attributes.payableAmount || 0,
        service_fee_amount: splitBillData?.serviceFeeAmount || 0,
        brandName: splitBillData?.brandName || '',
        orderId: splitBillData?.orderId || attributes.merchantReference || '',
      };

      window.gtag('event', 'purchase_failed', failureData);
    };

    // If generic error, wait for real error from API
    const currentErrorMessage = attributes.errorMessage || attributes.message || '';
    if (currentErrorMessage === 'Authentication process for the transaction failed.') {
      setTimeout(sendFailureEvent, 1000); // Wait 1 second for real error
    } else {
      sendFailureEvent(); // Send immediately if non-generic error
    }
  }

  // Save payment method when payment succeeds
  if (
    type === 'transaction' &&
    attributes.eventName === 'paymentSuccess' &&
    attributes.paymentMethod
  ) {
    lastPaymentMethod = mapPaymentMethodToCode(attributes.paymentMethod);
    // Set payment end timestamp for successful payments
    if (!paymentEndTimestamp) {
      paymentEndTimestamp = Date.now();
    }
    // Note: Don't reset failed payments counter here - it will be reset after the purchase_completed event is sent
  }

  switch (type) {
    case 'error':
    case 'transaction':
      sendBusinessEvent(`${APP_TYPE}.${type}`, dataWithAppId);
      break;

    default:
      console.log('[Payment Monitoring] default:', type, dataWithAppId);
  }
};
